import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';

const DEFAULT_PORTAL_ID = 'confirmation-root';

interface ConfirmationPortalProps {
  children: ReactNode;
  containerId?: string;
}

const resolveContainer = (containerId: string): HTMLDivElement | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  const existingContainer = document.getElementById(containerId);
  if (existingContainer instanceof HTMLDivElement) {
    return existingContainer;
  }

  const createdContainer = document.createElement('div');
  createdContainer.id = containerId;
  createdContainer.dataset.portalOwner = 'confirmation';
  document.body.appendChild(createdContainer);

  return createdContainer;
};

const ConfirmationPortal = ({
  children,
  containerId = DEFAULT_PORTAL_ID,
}: ConfirmationPortalProps) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const target = resolveContainer(containerId);
    setContainer(target);

    return () => {
      if (target?.dataset.portalOwner === 'confirmation') {
        target.remove();
      }
    };
  }, [containerId]);

  if (!container) {
    return null;
  }

  return createPortal(children, container);
};

export default ConfirmationPortal;
